import { Component, Input, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IPermissionGroup } from 'src/app/authentication/models/permission-role';
import { RoleService } from 'src/app/authentication/services/role.service';

/** Where a permission comes from - drives the marker in the detail list. */
export type PermissionOrigin = 'implicit' | 'group' | 'explicit' | 'excluded' | 'none';

interface PermissionRow {
  name: string;
  displayName: string;
  granted: boolean;
  origin: PermissionOrigin;
}

interface GroupCategory {
  name: string;
  groups: IPermissionGroup[];
}

/**
 * Composes a security group out of permission groups.
 *
 * The base rights apply to every security group and are therefore not selectable, only revocable.
 * Everything the user ticks or unticks in the detail list is sent as the desired EFFECTIVE set;
 * the backend derives from it what becomes an individual grant and what becomes an exception.
 */
@Component({
    selector: 'app-permission-role-edit',
    templateUrl: './permission-role-edit.component.html',
    styleUrls: ['./permission-role-edit.component.css'],
    standalone: false
})
export class PermissionRoleEditComponent implements OnInit {

  @Input() permissionId: string;

  categories: GroupCategory[] = [];
  permissionRows: PermissionRow[] = [];

  selectedGroupKeys = new Set<string>();
  showDetails = false;
  saving = false;
  saved = false;

  private groupsByKey = new Map<string, IPermissionGroup>();
  private implicitPermissions = new Set<string>();
  private explicitPermissions = new Set<string>();
  private excludedPermissions = new Set<string>();
  private assignablePermissions: string[] = [];

  constructor(private permissionRoleService: RoleService) {}

  ngOnInit(): void {
    this.initData();
  }

  initData() {
    forkJoin({
      composition: this.permissionRoleService.getRoleGroups(this.permissionId),
      groups: this.permissionRoleService.getAvailablePermissionGroups(),
      assignable: this.permissionRoleService.getAvailablePermissions()
    }).subscribe(({ composition, groups, assignable }) => {
      this.groupsByKey = new Map((groups ?? []).map(g => [g.key, g]));
      this.categories = this.toCategories(groups ?? []);

      this.selectedGroupKeys = new Set(composition?.groups ?? []);
      this.implicitPermissions = new Set(composition?.implicitPermissions ?? []);
      this.explicitPermissions = new Set(composition?.explicitPermissions ?? []);
      this.excludedPermissions = new Set(composition?.excludedPermissions ?? []);

      // The detail list covers everything assignable plus the base rights, which are not part of
      // the assignable list because they are never selected explicitly.
      this.assignablePermissions = Array.from(
        new Set([...(assignable ?? []), ...this.implicitPermissions]));

      this.rebuildRows();
    });
  }

  get selectedGroupCount(): number {
    return this.selectedGroupKeys.size;
  }

  get grantedCount(): number {
    return this.permissionRows.filter(r => r.granted).length;
  }

  get exceptionRows(): PermissionRow[] {
    return this.permissionRows.filter(r => r.origin === 'excluded');
  }

  isGroupSelected(key: string): boolean {
    return this.selectedGroupKeys.has(key);
  }

  toggleGroup(key: string, checked: boolean): void {
    if (checked) {
      this.selectedGroupKeys.add(key);
    } else {
      this.selectedGroupKeys.delete(key);
    }

    this.saved = false;
    this.rebuildRows();
  }

  /**
   * Mirrors what the backend derives on save: a right the composition already grants becomes an
   * exception when unticked, anything else becomes an individual grant when ticked.
   */
  togglePermission(row: PermissionRow, checked: boolean): void {
    const granted = this.grantedByComposition();

    if (checked) {
      this.excludedPermissions.delete(row.name);
      if (!granted.has(row.name)) {
        this.explicitPermissions.add(row.name);
      }
    } else {
      this.explicitPermissions.delete(row.name);
      if (granted.has(row.name)) {
        this.excludedPermissions.add(row.name);
      }
    }

    this.saved = false;
    this.rebuildRows();
  }

  toggleDetails(): void {
    this.showDetails = !this.showDetails;
  }

  save(): void {
    this.saving = true;
    this.saved = false;

    const groups = Array.from(this.selectedGroupKeys);
    const permissions = this.permissionRows.filter(r => r.granted).map(r => r.name);

    // Groups first: the exceptions are derived against what the groups grant, so they have to be
    // stored before the effective set is posted.
    this.permissionRoleService.updateRoleGroups({ roleId: this.permissionId, groups })
      .pipe(switchMap(() => this.permissionRoleService.updateRolesPermission({
        roleId: this.permissionId,
        permissions
      })))
      .subscribe({
        next: () => {
          this.saving = false;
          this.saved = true;
          this.initData();
        },
        error: () => {
          this.saving = false;
        }
      });
  }

  private toCategories(groups: IPermissionGroup[]): GroupCategory[] {
    const byCategory = new Map<string, IPermissionGroup[]>();

    for (const group of groups) {
      const bucket = byCategory.get(group.category) ?? [];
      bucket.push(group);
      byCategory.set(group.category, bucket);
    }

    return Array.from(byCategory.entries())
      .map(([name, items]) => ({ name, groups: items.sort((a, b) => a.order - b.order) }))
      .sort((a, b) => Math.min(...a.groups.map(g => g.order)) - Math.min(...b.groups.map(g => g.order)));
  }

  /** Base rights plus everything the selected permission groups contribute. */
  private grantedByComposition(): Set<string> {
    const granted = new Set<string>(this.implicitPermissions);

    for (const key of this.selectedGroupKeys) {
      for (const permission of this.groupsByKey.get(key)?.permissions ?? []) {
        granted.add(permission);
      }
    }

    return granted;
  }

  private rebuildRows(): void {
    const granted = this.grantedByComposition();

    this.permissionRows = this.assignablePermissions
      .map(name => {
        const origin = this.originOf(name, granted);
        return {
          name,
          displayName: this.formatPermission(name),
          granted: origin !== 'excluded' && origin !== 'none',
          origin
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  private originOf(name: string, granted: Set<string>): PermissionOrigin {
    if (this.excludedPermissions.has(name)) {
      return 'excluded';
    }

    if (this.explicitPermissions.has(name)) {
      return 'explicit';
    }

    if (this.implicitPermissions.has(name)) {
      return 'implicit';
    }

    return granted.has(name) ? 'group' : 'none';
  }

  private formatPermission(permission: string): string {
    return permission.replace(/^Permissions\./, '').replace(/\./g, ' · ');
  }
}
